import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Paper, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const TIPOS = ['Vacaciones', 'Incapacidad', 'Permiso', 'Dia libre'];

const TIPO_COLOR = {
  Vacaciones:  'success',
  Incapacidad: 'error',
  Permiso:     'warning',
  'Dia libre': 'info',
};

const initialForm = {
  operario: '',
  tipo: '',
  fechaInicio: '',
  fechaFin: '',
  nota: '',
};

const fmtFecha = (iso) =>
  iso ? new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—';

const AusenciasOperario = () => {
  const [form, setForm]             = useState(initialForm);
  const [ausencias, setAusencias]   = useState([]);
  const [operarios, setOperarios]   = useState([]);
  const [editId, setEditId]         = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId]     = useState(null);
  const [error, setError]           = useState('');
  const [exito, setExito]           = useState('');

  const token = () => localStorage.getItem('token');

  useEffect(() => {
    fetchAusencias();
    fetchOperarios();
  }, []);

  const fetchAusencias = async () => {
    try {
      const res = await fetch('/api/ausencias-operario', {
        credentials: 'include',
        headers: { 'x-access-token': token() },
      });
      const data = await res.json();
      setAusencias(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setAusencias([]);
    }
  };

  const fetchOperarios = async () => {
    try {
      const res = await fetch('/api/operators', {
        credentials: 'include',
        headers: { 'x-access-token': token() },
      });
      const data = await res.json();
      setOperarios(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setOperarios([]);
    }
  };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
    setExito('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.operario || !form.tipo || !form.fechaInicio || !form.fechaFin) {
      setError('Operario, tipo, fecha de inicio y fecha de fin son obligatorios');
      return;
    }
    if (form.fechaFin < form.fechaInicio) {
      setError('La fecha de fin no puede ser anterior a la de inicio');
      return;
    }
    const url    = editId ? `/api/ausencias-operario/${editId}` : '/api/ausencias-operario';
    const method = editId ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'x-access-token': token() },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setForm(initialForm);
        setEditId(null);
        setExito(data.message || 'Guardado correctamente');
        fetchAusencias();
      } else {
        setError(data.message || 'Error al guardar');
      }
    } catch (err) {
      setError('Error de conexión');
    }
  };

  const handleEdit = (a) => {
    setForm({
      operario:    a.operario?._id || a.operario,
      tipo:        a.tipo,
      fechaInicio: a.fechaInicio?.slice(0, 10) || '',
      fechaFin:    a.fechaFin?.slice(0, 10)    || '',
      nota:        a.nota || '',
    });
    setEditId(a._id);
    setError('');
    setExito('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => { setForm(initialForm); setEditId(null); setError(''); };

  const confirmDelete = (id) => { setDeleteId(id); setOpenDialog(true); };

  const handleDelete = async () => {
    try {
      await fetch(`/api/ausencias-operario/${deleteId}`, {
        method: 'DELETE',
        headers: { 'x-access-token': token() },
        credentials: 'include',
      });
    } catch (err) {
      console.error(err);
    }
    setOpenDialog(false);
    setDeleteId(null);
    fetchAusencias();
  };

  // Determinar si una ausencia está activa hoy
  const esActiva = (a) => {
    const hoy = new Date();
    return new Date(a.fechaInicio) <= hoy && new Date(a.fechaFin) >= hoy;
  };

  return (
    <Box sx={{ maxWidth: 950, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Ausencias de Operarios
      </Typography>

      {/* Formulario */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {editId ? 'Editar ausencia' : 'Registrar ausencia'}
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>

          {/* Operario */}
          <TextField
            select
            label="Operario"
            name="operario"
            value={form.operario}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value="">-- Selecciona un operario --</MenuItem>
            {operarios.map(op => (
              <MenuItem key={op._id} value={op._id}>
                {op.name} {op.lastName} {op.cargo ? `— ${op.cargo}` : ''}
              </MenuItem>
            ))}
          </TextField>

          {/* Tipo */}
          <TextField
            select
            label="Tipo de ausencia"
            name="tipo"
            value={form.tipo}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value="">-- Selecciona un tipo --</MenuItem>
            {TIPOS.map(t => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>

          {/* Fechas */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Fecha de inicio"
              name="fechaInicio"
              type="date"
              value={form.fechaInicio}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="Fecha de fin"
              name="fechaFin"
              type="date"
              value={form.fechaFin}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
          </Box>

          {/* Nota */}
          <TextField
            label="Nota (opcional)"
            name="nota"
            value={form.nota}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {exito && <Alert severity="success" sx={{ mb: 2 }}>{exito}</Alert>}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button type="submit" variant="contained">
              {editId ? 'Actualizar' : 'Registrar'}
            </Button>
            {editId && (
              <Button variant="outlined" onClick={handleCancelEdit}>Cancelar</Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Operario</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Cargo</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Desde</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Hasta</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Nota</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ausencias.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: '#888' }}>
                  No hay ausencias registradas
                </TableCell>
              </TableRow>
            ) : (
              ausencias.map(a => (
                <TableRow key={a._id} hover>
                  <TableCell>
                    {a.operario?.name} {a.operario?.lastName}
                  </TableCell>
                  <TableCell sx={{ color: '#888', fontSize: 12 }}>
                    {a.operario?.cargo || '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={a.tipo}
                      size="small"
                      color={TIPO_COLOR[a.tipo] || 'default'}
                    />
                  </TableCell>
                  <TableCell>{fmtFecha(a.fechaInicio)}</TableCell>
                  <TableCell>{fmtFecha(a.fechaFin)}</TableCell>
                  <TableCell>
                    {esActiva(a)
                      ? <Chip label="Activa" size="small" color="warning" />
                      : <Chip label="Finalizada" size="small" variant="outlined" />}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 160, fontSize: 12, color: '#888' }}>
                    {a.nota || '—'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEdit(a)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => confirmDelete(a._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo confirmar eliminación */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Eliminar esta ausencia del registro?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AusenciasOperario;
